/**
 * 通用 WebSocket 服务
 * 功能：
 * 1. 连接时携带 token 认证
 * 2. 自动重连机制
 * 3. 心跳检测
 * 4. 发送/接收消息
 * 5. 事件监听系统
 */

import { merge } from 'lodash';
import { BASE_WS_URL } from './config';

type MessageHandler = (data: any) => void;
type ErrorHandler = (error: Event) => void;
type CloseHandler = (event: CloseEvent) => void;
type OpenHandler = (event: Event) => void;
interface WebSocketConfig {
  url: string; // WebSocket 服务地址
  tokenKey?: string; // localStorage 中 token 的 key，默认 'token'
  heartbeatInterval?: number; // 心跳间隔(ms)，默认 30000
  reconnectInterval?: number; // 重连间隔(ms)，默认 5000
  reconnectAttempts?: number; // 最大重连次数，默认 5，设为 -1 表示无限重连
  debug?: boolean; // 是否开启调试日志
  onMessage?: MessageHandler; // 消息接收回调
  onError?: ErrorHandler; // 错误回调
  onClose?: CloseHandler; // 关闭回调
  onOpen?: OpenHandler; // 连接成功回调
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private reconnectCount = 0;
  private isManualClose = false; // 是否手动关闭
  private messageHandlers: Set<MessageHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();
  private closeHandlers: Set<CloseHandler> = new Set();
  private openHandlers: Set<OpenHandler> = new Set();

  constructor(config: WebSocketConfig) {
    const defaultConfig = {
      heartbeatInterval: 30000,
      reconnectInterval: 5000,
      reconnectAttempts: 5,
      debug: true,
      tokenKey: 'token',
      onMessage: () => {},
      onError: () => {},
      onClose: () => {},
      onOpen: () => {},
    };
    this.config = merge(defaultConfig, config);
    // console.log(this.config);

    // 添加默认的事件处理器
    if (this.config.onMessage) {
      this.messageHandlers.add(this.config.onMessage);
    }
    if (this.config.onError) {
      this.errorHandlers.add(this.config.onError);
    }
    if (this.config.onClose) {
      this.closeHandlers.add(this.config.onClose);
    }
    if (this.config.onOpen) {
      this.openHandlers.add(this.config.onOpen);
    }
  }

  /**
   * 连接 WebSocket
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.log('WebSocket 已经连接');
      return;
    }

    try {
      // 构建带 token 的 URL
      const url = this.buildUrl();
      this.log('正在连接 WebSocket:', url);

      this.ws = new WebSocket(url);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      this.log('WebSocket 连接失败:', error);
      this.reconnect();
    }
  }

  /**
   * 构建带 token 的 URL
   */
  private buildUrl(): string {
    const { url, tokenKey } = this.config;

    const baseUrl = !url.startsWith('ws') ? BASE_WS_URL : '';

    // 从 localStorage 获取 token
    const token = localStorage.getItem(tokenKey);

    if (!token) {
      this.log('警告: localStorage 中未找到 token');
      return baseUrl + url;
    }

    // 判断 URL 中是否已有查询参数
    const separator = url.includes('?') ? '&' : '?';
    return `${baseUrl}${url}${separator}token=${encodeURIComponent(token)}`;
  }

  /**
   * 处理连接成功
   */
  private handleOpen(event: Event): void {
    this.log('WebSocket 连接成功');
    this.reconnectCount = 0; // 重置重连计数
    this.startHeartbeat(); // 开启心跳

    // 触发所有的 open 事件处理器
    this.openHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        this.log('Open handler 执行出错:', error);
      }
    });
  }

  /**
   * 处理接收消息
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      this.log('收到消息:', data);

      // 触发所有的 message 事件处理器
      this.messageHandlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          this.log('Message handler 执行出错:', error);
        }
      });
    } catch (error) {
      this.log('消息解析失败:', error);
    }
  }

  /**
   * 处理错误
   */
  private handleError(event: Event): void {
    this.log('WebSocket 错误:', event);

    // 触发所有的 error 事件处理器
    this.errorHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        this.log('Error handler 执行出错:', error);
      }
    });
  }

  /**
   * 处理连接关闭
   */
  private handleClose(event: CloseEvent): void {
    this.log('WebSocket 连接关闭:', event.code, event.reason);
    this.stopHeartbeat();

    // 触发所有的 close 事件处理器
    this.closeHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        this.log('Close handler 执行出错:', error);
      }
    });

    // 如果不是手动关闭，则尝试重连
    if (!this.isManualClose) {
      this.reconnect();
    }
  }

  /**
   * 重连
   */
  private reconnect(): void {
    const { reconnectAttempts, reconnectInterval } = this.config;

    // 检查是否超过最大重连次数（-1 表示无限重连）
    if (reconnectAttempts !== -1 && this.reconnectCount >= reconnectAttempts) {
      this.log('已达到最大重连次数，停止重连');
      return;
    }

    // 清除之前的重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectCount++;
    this.log(
      `准备第 ${this.reconnectCount} 次重连，${reconnectInterval}ms 后执行...`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, reconnectInterval);
  }

  /**
   * 开启心跳
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
        this.log('发送心跳');
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * 停止心跳
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 发送消息
   * @param data 要发送的数据
   */
  send(data: API.RobotMqttMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      this.log('WebSocket 未连接，无法发送消息');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.ws.send(message);
      this.log('发送消息:', data);
      return true;
    } catch (error) {
      this.log('发送消息失败:', error);
      return false;
    }
  }

  /**
   * 手动关闭连接
   */
  close(): void {
    this.isManualClose = true;
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.log('WebSocket 已手动关闭');
  }

  /**
   * 重新连接（会重置重连计数）
   */
  reconnectManually(): void {
    this.isManualClose = false;
    this.reconnectCount = 0;
    this.close();
    this.connect();
  }

  /**
   * 添加消息监听器
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    // 返回取消监听的函数
    return () => this.offMessage(handler);
  }

  /**
   * 移除消息监听器
   */
  offMessage(handler: MessageHandler): void {
    this.messageHandlers.delete(handler);
  }

  /**
   * 添加错误监听器
   */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.offError(handler);
  }

  /**
   * 移除错误监听器
   */
  offError(handler: ErrorHandler): void {
    this.errorHandlers.delete(handler);
  }

  /**
   * 添加关闭监听器
   */
  onClose(handler: CloseHandler): () => void {
    this.closeHandlers.add(handler);
    return () => this.offClose(handler);
  }

  /**
   * 移除关闭监听器
   */
  offClose(handler: CloseHandler): void {
    this.closeHandlers.delete(handler);
  }

  /**
   * 添加连接成功监听器
   */
  onOpen(handler: OpenHandler): () => void {
    this.openHandlers.add(handler);
    return () => this.offOpen(handler);
  }

  /**
   * 移除连接成功监听器
   */
  offOpen(handler: OpenHandler): void {
    this.openHandlers.delete(handler);
  }

  /**
   * 获取当前连接状态
   */
  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  /**
   * 判断是否已连接
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * 调试日志
   */
  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[WebSocket]', ...args);
    }
  }
}

// 创建单例 WebSocket 服务（可选）
let defaultWebSocketService: WebSocketService | null = null;

/**
 * 创建默认的 WebSocket 服务实例
 */
export function createDefaultWebSocketService(
  config: WebSocketConfig,
): WebSocketService {
  if (defaultWebSocketService) {
    defaultWebSocketService.close();
  }
  defaultWebSocketService = new WebSocketService(config);
  return defaultWebSocketService;
}

/**
 * 获取默认的 WebSocket 服务实例
 */
export function getDefaultWebSocketService(): WebSocketService | null {
  return defaultWebSocketService;
}

export default WebSocketService;
